
import { Component, Vue } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import { DataTableHeader } from 'vuetify';
import WebsiteRepositoryInterface, {
  Website,
  WebsiteCreation,
  WebsiteCreationAction, WebsiteDeletion,
  WebsiteDeletionAction,
  WebsiteOrchestratorResourceInterface,
  WebsiteOrchestratorResourceType,
} from '@/services/Website/interfaces';
import {
  OrchestratorActionStatus, OrchestratorActionType,
  WebsiteMigration,
  WebsiteMigrationAction, WebsiteMigrationActionMessageInterface, WebsiteMigrationActionMessagePayloadInterface,
} from '@/services/WebsiteMigrations/interfaces';
import ChangeActionStatusDialog from '@/components/dialogs/website-orchestrator/ChangeActionStatusDialog.vue';
import VComponent from '@/components/base/VComponent.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import DeletionOrchestratorStatus from '@/components/website/DeletionOrchestratorStatus.vue';
import WebsiteErrorStatusHover from '@/components/website/WebsiteErrorStatusHover.vue';
import {
  OrchestratorRowState,
  collectRelatedWebsiteIds,
  fetchLatestOrchestrator,
} from '@/utils/websiteOrchestrator';

type ActionTiming = { start: number; end: number, parents: number[] };

interface WebsiteCreationActionMessagePayloadInterface {
  id: number,
  status: string,
  logs: string[],
}

interface WebsiteCreationActionMessageInterface {
  payload: WebsiteCreationActionMessagePayloadInterface;
}

@Component({
  mixins: [Themeable],
  inject: [
    'websiteRepository',
  ],
  components: {
    PageHeader,
    VComponent,
    ChangeActionStatusDialog,
    DeletionOrchestratorStatus,
    WebsiteErrorStatusHover,
  },
  sockets: {
    websiteMigrationAction(msg: WebsiteMigrationActionMessageInterface): void {
      this.$emit('actionReceived', msg.payload);
    },
    websiteCreationAction(msg: WebsiteCreationActionMessageInterface): void {
      this.$emit('actionReceived', msg.payload);
    },
  },
})
export default class ViewResource extends Vue {
  public loading = true;

  public relatedLoading = false;

  public websiteRepository!: WebsiteRepositoryInterface;

  public websiteId: number;

  public resourceId!: number;

  public resourceType!: number;

  public resource!: WebsiteOrchestratorResourceInterface;

  public actions: WebsiteCreationAction[] | WebsiteDeletionAction[] | WebsiteMigrationAction[] = [];

  public actionTimesMap: Map<number, string> = new Map();

  public relatedWebsites: Website[] = [];

  public orchestratorMap: { [websiteId: number]: OrchestratorRowState } = {};

  public websiteHeaders: DataTableHeader[] = [
    { text: 'Id', value: 'id', width: '80px' },
    { text: 'Domain', value: 'domain' },
    { text: 'Status', value: 'status', width: '120px' },
    { text: 'Orchestrator', value: 'orchestrator', sortable: false, width: '280px' },
  ];

  public resourceMap: string[] = [
    'CREATION',
    'DELETION',
    'MIGRATION',
  ];

  public actionsMap: { [key: string]: string } = {
    'create_dns': 'CREATE DNS',
    'delete_dns': 'DELETE DNS',
    'migrate_dns': 'MIGRATE DNS',
    'create_unix_user': 'CREATE UNIX USER',
    'delete_unix_user': 'DELETE UNIX USER',
    'migrate_unix_user': 'MIGRATE UNIX USER',
    'create_database': 'CREATE DATABASE',
    'delete_database': 'DELETE DATABASE',
    'migrate_database': 'MIGRATE DATABASE',
    'create_vhost': 'CREATE VHOST',
    'delete_vhost': 'DELETE VHOST',
    'migrate_vhost': 'MIGRATE VHOST',
    'create_redis_cache': 'CREATE REDIS CACHE',
    'migrate_redis_cache': 'MIGRATE REDIS CACHE',
    'delete_redis_cache': 'DELETE REDIS CACHE',
    'create_email': 'CREATE EMAIL',
    'delete_email': 'DELETE EMAIL',
    'migrate_email': 'MIGRATE EMAIL',
    'create_letsencrypt': 'CREATE LETS ENCRYPT',
    'delete_letsencrypt': 'DELETE LETS ENCRYPT',
    'migrate_letsencrypt': 'MIGRATE SSL',
    'activate_cdn': `ACTIVATE CDN`,
    'deactivate_cdn': `DEACTIVATE CDN`,
    'migrate_cdn': 'MIGRATE CDN',
    'install_wp': 'INSTALL WP',
    'uninstall_wp': 'UNINSTALL WP',
    'clone_wp': 'CLONE WP',
    'delete_ftp': 'DELETE FTP',
    'migrate_ftp': 'MIGRATE FTP',
    'approve_migration_data': 'APPROVE MIGRATION DATA',
    'approve_website_migration': 'APPROVE WEBSITE MIGRATION',
  };

  public actionsStatusMap: { [key: string]: string } = {
    'pending': 'PENDING',
    'in progress': 'IN PROGRESS',
    'failed': 'FAILED',
    'retry': 'RETRY',
    'retrying': 'RETRYING',
    'done': 'DONE',
    'rolling back': 'ROLLING BACK',
    'rolled back': 'ROLLED BACK',
    'cancelled': 'CANCELLED',
  };

  public actionStatusIconMap: { [key: string]: string } = {
    'pending': 'mdi-progress-clock',
    'in progress': 'mdi-progress-helper',
    'failed': 'mdi-close-thick',
    'retry': 'mdi-repeat',
    'retrying': 'mdi-repeat-variant',
    'done': 'mdi-check',
    'rolling back': 'mdi-arrow-u-left-top',
    'rolled back': 'mdi-backup-restore',
    'cancelled': 'mdi-cancel',
  };

  public changeActionStatusDialog: string | null = null;
  public actionForChangeStatusDialog!: WebsiteCreationAction | WebsiteDeletionAction | WebsiteMigrationAction;

  public async created(): Promise<void> {
    try {
      this.$on('actionReceived', this.handleWSActionMessage);

      this.websiteId = Number(this.$route.params.websiteId);
      this.resourceType = Number(this.$route.params.resourceType);
      this.resourceId = Number(this.$route.params.resourceId);

      await this.loadResource();
      await this.loadRelatedWebsitesAndOrchestrator();

      this.loading = false;
    } catch (err) {
      this.$errorDialog.show(err.message);
    }
  }

  public orchLoading(websiteId: number): boolean {
    const row = this.orchestratorMap[websiteId];
    return Boolean(row && row.loading);
  }

  public orchCreation(websiteId: number): WebsiteCreation | null {
    const row = this.orchestratorMap[websiteId];
    return row && row.creation ? row.creation : null;
  }

  public orchDeletion(websiteId: number): WebsiteDeletion | null {
    const row = this.orchestratorMap[websiteId];
    return row && row.deletion ? row.deletion : null;
  }

  private relatedIdsFromResource(): number[] {
    if (!this.resource) {
      return [];
    }
    return collectRelatedWebsiteIds(
      this.resource.creation ? [this.resource.creation] : [],
      this.resource.deletion ? [this.resource.deletion] : [],
      this.resource.migration ? [this.resource.migration] : [],
    );
  }

  private async loadRelatedWebsitesAndOrchestrator(): Promise<void> {
    const ids = this.relatedIdsFromResource();
    if (!ids.length) {
      return;
    }

    this.relatedLoading = true;
    try {
      const websites = await this.websiteRepository.getWebsitesByIds(ids);
      const map: { [id: number]: Website } = {};
      websites.forEach((website) => {
        map[website.id] = website;
      });
      this.relatedWebsites = ids
        .map((id) => map[id])
        .filter((website): website is Website => Boolean(website));
    } catch (err) {
      this.relatedLoading = false;
      return;
    } finally {
      this.relatedLoading = false;
    }

    const nextMap: { [websiteId: number]: OrchestratorRowState } = {};
    this.relatedWebsites.forEach((website) => {
      nextMap[website.id] = { loading: true, creation: null, deletion: null };
    });
    this.orchestratorMap = { ...nextMap };

    for (let i = 0; i < this.relatedWebsites.length; i += 1) {
      const website = this.relatedWebsites[i];
      try {
        const resources = await fetchLatestOrchestrator(this.websiteRepository, website.id, false);
        this.$set(this.orchestratorMap, website.id, {
          loading: false,
          creation: resources.creation,
          deletion: resources.deletion,
        });
      } catch (err) {
        this.$set(this.orchestratorMap, website.id, {
          loading: false,
          creation: null,
          deletion: null,
        });
      }
    }
  }

  public async loadResource(): Promise<void> {
    const resource = await this.websiteRepository.getWebsiteOrchestratorResource(
        this.websiteId,
        this.resourceId,
        this.resourceType,
    );

    if (!resource.creation && !resource.deletion && !resource.migration) {
      throw new Error('Resource Not Found!');
    }

    if (resource.type === undefined) {
      resource.type = WebsiteOrchestratorResourceType.CREATION;
    }

    this.resource = resource;
    let task: WebsiteCreation | WebsiteDeletion | WebsiteMigration | undefined;

    switch (this.resource.type) {
      case WebsiteOrchestratorResourceType.CREATION:
        this.actions = this.resource.creation?.actions || [];
        task = this.resource.creation;
        break;
      case WebsiteOrchestratorResourceType.DELETION:
        this.actions = this.resource.deletion?.actions || [];
        task = this.resource.deletion;
        break;
      case WebsiteOrchestratorResourceType.MIGRATION:
        this.actions = this.resource.migration?.actions || [];
        task = this.resource.migration;
        break;
    }

    if (!task) {
      return;
    }

    this.calculateEffectiveTime(task);
  }

  public transformKebabCase(str: string): string {
    return str
        .replaceAll('-', ' ')
        .toUpperCase();
  }

  public getTimeDifference(dateStr1: string, dateStr2: string): string {
    const date1 = new Date(dateStr1);
    const date2 = new Date(dateStr2);

    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
      throw 'N/A';
    }

    const diffMs = Math.abs(date2.getTime() - date1.getTime());

    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    const milliseconds = diffMs % 1000;


    return `${minutes}m ${seconds}s ${milliseconds}ms`;
  }

  public calculateEffectiveTime(task: WebsiteCreation | WebsiteMigration | WebsiteDeletion): void {
    const parseTime = (dateStr: string) => new Date(dateStr).getTime();

    const actionMap = new Map();
    let totalEffectiveTime = 0;

    task.actions.forEach(action => {
      actionMap.set(action.id, {
        start: parseTime(action.createdDate),
        end: parseTime(action.updatedDate),
        parents: action.parentActionsIds || [],
      });
    });

    const actionTimes: Map<number, string> = new Map();
    task.actions.forEach((action) => {
      const createdDate = parseTime(action.createdDate);
      const updatedDate = parseTime(action.updatedDate);

      const start = Math.min(createdDate, updatedDate);
      const end = Math.max(createdDate, updatedDate);
      let elapsedTime = end - start;

      let deductedTime = this.getLastParentTimeOverlap(
          actionMap,
          {
            start,
            end,
            parents: action.parentActionsIds || [],
          },
      );

      let effectiveTime = elapsedTime - deductedTime;
      totalEffectiveTime += effectiveTime;

      const minutes = Math.floor(effectiveTime / 60000);
      const seconds = Math.floor((effectiveTime % 60000) / 1000);
      const milliseconds = effectiveTime % 1000;

      let formattedTime = '';

      if (minutes > 0) {
        formattedTime = `${formattedTime} ${minutes}m`;
      }

      if (seconds > 0) {
        formattedTime = `${formattedTime} ${seconds}s`;
      }

      if (milliseconds > 0) {
        formattedTime = `${formattedTime} ${milliseconds}ms`;
      }

      actionTimes.set(action.id, formattedTime);
    });

    let totalFormattedTime = `${Math.floor(totalEffectiveTime / 60000)}m ${Math.floor((totalEffectiveTime % 60000) / 1000)}s ${totalEffectiveTime % 1000}ms`;
    actionTimes.set(0, totalFormattedTime);

    this.actionTimesMap = actionTimes;
  }

  private getLastParentTimeOverlap(actionMap: Map<number, ActionTiming>, action: ActionTiming): number {
    if (!action.parents.length) {
      return 0;
    }

    const lastParentId = action.parents[action.parents.length - 1];
    if (!actionMap.has(lastParentId)) {
      return 0;
    }

    const parent = actionMap.get(lastParentId);
    if (!parent) {
      return 0;
    }

    const overlapStart = Math.max(parent.start, action.start);
    const overlapEnd = Math.min(parent.end, action.end);

    return Math.max(0, overlapEnd - overlapStart);
  }

  public showChangeActionStatusDialog(
      action: WebsiteCreationAction | WebsiteDeletionAction | WebsiteMigrationAction,
  ): void {
    this.changeActionStatusDialog = 'change-action-status-dialog';
    this.actionForChangeStatusDialog = action;
  }

  public async changeActionStatus(actionId: number, status: OrchestratorActionStatus): Promise<void> {
    try {
      const action = this.actions.find((a) => a.id === actionId);
      if (!action) {
        this.$errorDialog.show('Action Not Fond!');

        return;
      }

      await this.websiteRepository.updateActionStatus(
          this.websiteId,
          actionId,
          this.resourceId,
          this.resourceMap[this.resourceType].toLowerCase() as unknown as OrchestratorActionType,
          status,
      );

      action.status = status;

      this.$successDialog.show('Action status update started!');
    } catch (err) {
      this.$errorDialog.show(err.message);
    }
  }

  public handleWSActionMessage(action: WebsiteMigrationActionMessagePayloadInterface): void {
    const actionIndex = this.actions.findIndex((a) => a.id === action.id);
    if (actionIndex === -1) {
      return;
    }

    this.actions[actionIndex].status = action.status;

    if (!action.logs || !action.logs.length) {
      return;
    }

    if (!this.actions[actionIndex].logs) {
      this.actions[actionIndex].logs = [];
    }

    this.actions[actionIndex].logs.push(...action.logs.map((l) => ({
      id: -1,
      log: l,
      createdDate: (new Date()).toISOString(),
    })));
  }
}
